import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { COMMON_ERROR } from 'utils/ErrorMessages'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { addToast } from 'containers/Toasts/actions'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../PartnerOptions/constants'

import { clearAddMailboxModalValues } from './AddMailbox/actions'
import tableRowEpics from './TableRow/epics'
import { TAGS_FORM_SUBMIT_ASYNC } from './TagsForm/constants'
import tagsFormEpics from './TagsForm/epics'
import { UPDATE_DATA_REQUEST_ASYNC } from './VipDiscovery/constants'
import vipDiscoveryEpics from './VipDiscovery/epics'
import {
  createComplianceReportError,
  createComplianceReportSuccess,
  fetchData,
  fetchDataError,
  fetchDataSuccess,
  fetchManagementTagsError,
  fetchManagementTagsSuccess,
  fetchProfilesSuggestionsError,
  fetchProfilesSuggestionsSuccess,
  statusesToggleError,
  statusesToggleSuccess,
} from './actions'
import {
  ADD_ASYNC,
  CREATE_COMPLIANCE_REPORT_ASYNC,
  DATA_REQUEST_ASYNC,
  FETCH_MANAGEMENT_TAGS_ASYNC,
  FETCH_PROFILES_SUGGESTIONS_ASYNC,
  PAGINATE_CHANGE,
  PROTECTED_FILTER_CHANGE,
  RISK_LEVELS_FILTER_CHANGE,
  SEARCH_CHANGE,
  SEGMENTS_FILTER_CHANGE,
  SORT_PROPERTY_CHANGE,
  STATUSES_TOGGLE_ASYNC,
  TAGS_FILTER_CHANGE,
  TYPE_FILTER_CHANGE,
} from './constants'
import {
  selectComplianceReportData,
  selectManagementTagsPage,
  selectManagementTagsSearch,
} from './selectors'

const fetchDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      DATA_REQUEST_ASYNC.INITIATE,
      SORT_PROPERTY_CHANGE,
      TYPE_FILTER_CHANGE,
      PROTECTED_FILTER_CHANGE,
      TAGS_FILTER_CHANGE,
      SEGMENTS_FILTER_CHANGE,
      RISK_LEVELS_FILTER_CHANGE,
      PAGINATE_CHANGE,
      SEARCH_CHANGE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      UPDATE_DATA_REQUEST_ASYNC.SUCCESS,
      STATUSES_TOGGLE_ASYNC.SUCCESS,
      TAGS_FORM_SUBMIT_ASYNC.SUCCESS
    )
    .filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.mailboxes.management)
      return true
    })
    .switchMap(() =>
      api.mailboxes
        .get(
          store.getState().mailboxes.management.page || 1,
          store.getState().mailboxes.management.sortProperty,
          store.getState().mailboxes.management.typeFilter,
          store.getState().mailboxes.management.protectedFilter,
          store
            .getState()
            .mailboxes.management.tagsFilter.map((elem) => elem.value)
            .join(','),
          store
            .getState()
            .mailboxes.management.segmentsFilter.map((elem) => elem.value)
            .join(','),
          store
            .getState()
            .mailboxes.management.riskLevelsFilter.map((elem) => elem.value)
            .join(','),
          store.getState().mailboxes.management.search,
          store.getState().partnerOptions.selectedCompany?.value
        )
        .map((response) => fetchDataSuccess(response))
        .catch((error) => Observable.of(epicsErrorHandler(fetchDataError, error)))
        .takeUntil(action$.ofType(DATA_REQUEST_ASYNC.CANCEL))
    )

const statusesToggleEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(STATUSES_TOGGLE_ASYNC.INITIATE).mergeMap((action) =>
    api.mailboxes
      .toggleStatuses(store.getState().partnerOptions.selectedCompany?.value, {
        ids: Object.keys(store.getState().mailboxes.management.selectedMailboxesMap),
        active: action.payload.status,
        selectAll: store.getState().mailboxes.management.selectAll,
        typeFilter: store.getState().mailboxes.management.typeFilter,
        protectedFilter: store.getState().mailboxes.management.protectedFilter,
        tagsFilter: store.getState().mailboxes.management.selectAll
          ? store
              .getState()
              .mailboxes.management.tagsFilter.map((elem) => elem.value)
              .join(',')
          : '',
        segmentsFilter: store.getState().mailboxes.management.selectAll
          ? store
              .getState()
              .mailboxes.management.segmentsFilter.map((elem) => elem.value)
              .join(',')
          : '',
        riskLevelsFilter: store.getState().mailboxes.management.selectAll
          ? store
              .getState()
              .mailboxes.management.riskLevelsFilter.map((elem) => elem.value)
              .join(',')
          : '',
        search: store.getState().mailboxes.management.search,
      })
      .mergeMap(({ response }) =>
        Observable.of(
          statusesToggleSuccess(response || {}),
          addToast({
            message: response.message || 'Statuses updated successfully!',
            type: response.success ? 'success' : 'error',
          })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(statusesToggleError, error),
          addToast({
            message: error.response || COMMON_ERROR,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(STATUSES_TOGGLE_ASYNC.CANCEL))
  )

const addMailboxEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(ADD_ASYNC.INITIATE).mergeMap(() => {
    const state = store.getState()
    const formValues = state.form.AddMailboxForm.values
    const { selectedTitle, selectedDepartment, selectedAllTags } = state.mailboxes.addMailbox

    const payload = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email,
      phone: formValues.phone,
      ...(selectedTitle && { title: selectedTitle }), // Add title only if it's not null
      ...(selectedDepartment && { department: selectedDepartment }), // Add department only if it's not null
      ...(selectedAllTags?.length && { tags: selectedAllTags }), // Add tags only if it's not null
    }

    return api.mailboxes
      .add(store.getState().partnerOptions.selectedCompany?.value, payload)
      .map(({ response }) => {
        if (response.success) {
          store.dispatch(fetchData())
          store.dispatch(clearAddMailboxModalValues())
          return addToast({
            message: response.message || 'Mailbox added successfully!',
          })
        }
        return addToast({
          message: response.message || COMMON_ERROR,
          type: 'error',
        })
      })
      .catch((error) => Observable.of(epicsErrorHandler(ADD_ASYNC.onError, error)))
      .takeUntil(action$.ofType(ADD_ASYNC.CANCEL))
  })

const fetchProfilesSuggestionsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_PROFILES_SUGGESTIONS_ASYNC.INITIATE).mergeMap((action) =>
    api.mailboxes
      .getEmails(
        store.getState().partnerOptions.selectedCompany?.value,
        action.payload.search,
        false
      )
      .map((response) => fetchProfilesSuggestionsSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchProfilesSuggestionsError, error)))
      .takeUntil(action$.ofType(FETCH_PROFILES_SUGGESTIONS_ASYNC.CANCEL))
  )

const createComplianceReportEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CREATE_COMPLIANCE_REPORT_ASYNC.INITIATE).mergeMap((action) => {
    const complianceReportData = selectComplianceReportData(store.getState())

    return api.mailboxes
      .createComplianceReport(store.getState().partnerOptions.selectedCompany?.value, {
        reportType: complianceReportData.reportType,
        outputFormat: complianceReportData.exportFormatFilter,
        startDate: complianceReportData.startDate,
        endDate: complianceReportData.endDate,
        recipients: complianceReportData.recipients,
        message: complianceReportData.message,
      })
      .map((response) => createComplianceReportSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(createComplianceReportError, error)))
      .takeUntil(action$.ofType(CREATE_COMPLIANCE_REPORT_ASYNC.CANCEL))
  })

const fetchTagsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      FETCH_MANAGEMENT_TAGS_ASYNC.INITIATE,
      UPDATE_DATA_REQUEST_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      TAGS_FORM_SUBMIT_ASYNC.SUCCESS
    )
    .filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return window.location.href.indexOf(URL_CONST.mailboxes.self) !== -1
      return true
    })
    .mergeMap(() =>
      api.mailboxes
        .getTags({
          companyId: selectCompanyFilter(store.getState())?.value,
          allTags: false,
          page: selectManagementTagsPage(store.getState()),
          search: selectManagementTagsSearch(store.getState()),
        })
        .map((response) => fetchManagementTagsSuccess(response))
        .catch((error) => Observable.of(epicsErrorHandler(fetchManagementTagsError, error)))
        .takeUntil(action$.ofType(FETCH_MANAGEMENT_TAGS_ASYNC.CANCEL))
    )

export default combineEpics(
  tableRowEpics,
  tagsFormEpics,
  vipDiscoveryEpics,
  fetchDataEpic,
  statusesToggleEpic,
  addMailboxEpic,
  fetchProfilesSuggestionsEpic,
  createComplianceReportEpic,
  fetchTagsEpic
)
